import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Play } from 'lucide-react';

export const Team = () => {
  const teamMembers = [
    {
      name: 'Adisa Fazlovic',
      role: 'Executive Producer, Green Lens Collective',
      skills: 'Project Management · Client Relations · Strategy',
      description:
        'In charge of communication with clients, project development, and coordination of teams to ensure smooth execution.',
      image:
        'https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/99gx6nat_Captura%20de%20ecra%CC%83%202026-01-27%2C%20a%CC%80s%2020.33.21.png',
      linkedIn: 'https://www.linkedin.com/in/adisa-fazlovic/',
      reel: null,
      imagePosition: 'center',
    },
    {
      name: 'Márcio Loureiro',
      role: 'Film Director · Environmental Storytelling',
      description:
        'Dedicated to clear, human-centered documentary work supporting biodiversity action.',
      image:
        'https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/q69yjm8c_Mloureiro.png',
      linkedIn: 'https://www.linkedin.com/in/márcio-loureiro-99b39117/',
      reel: 'https://www.m-creative.net/',
      imagePosition: 'center',
    },
    {
      name: 'Rodrigo Delgado',
      role: 'Marine Biologist',
      description:
        'Marine biologist focused on the study and protection of marine ecosystems, working closely with communities and conservation initiatives.',
      image:
        'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/7sl3kxyp_marinebiology_student1-1024x559.png.webp',
      linkedIn: null,
      reel: null,
      imagePosition: 'center',
    },
    {
      name: 'Margarita Samsonova',
      role: 'Brand Ambassador · Social Media Strategist',
      description:
        'Margreen connects purpose-driven brands with real-world impact through authentic campaigns and a global greenfluencer network.',
      image:
        'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/hl30tzpt_openart-image_1774882903592_89fe6e56_1774882904431_c8333b30.png',
      linkedIn: 'https://www.linkedin.com/in/margreen/',
      reel: null,
      imagePosition: 'top',
    },
  ];

  return (
    <section
      id="team"
      className="bg-[#FDFCF8]"
      data-testid="team-section"
    >
      {/* Full-width Logo Brand Section */}
      <div className="bg-white w-full">
        <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/k31gl3t9_2.png"
              alt="Green Lens Collective - Environmental Media NGO"
              className="w-64 md:w-80 lg:w-96 h-auto"
              data-testid="team-logo"
            />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A2D23] tracking-tight mt-1">
              Meet Our Team
            </h2>
          </motion.div>
        </div>
      </div>

      {/* Team Members */}
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-16 md:py-20">

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white border border-[#E2E0D6] rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              data-testid={`team-member-${index}`}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  style={{ objectPosition: member.imagePosition === 'top' ? 'center 20%' : 'center center' }}
                />
              </div>
              <div className="p-6 space-y-2">
                <h3 className="text-xl font-semibold text-[#1A2D23]">
                  {member.name}
                </h3>
                <p className="text-sm font-medium text-[#2E5C44]">
                  {member.role}
                </p>
                {member.skills && (
                  <p className="text-xs font-medium text-[#2E5C44]/80">
                    {member.skills}
                  </p>
                )}
                {member.description && (
                  <p className="text-sm leading-relaxed text-[#5C6B63] pt-1">
                    {member.description}
                  </p>
                )}
                
                {/* Social Links - Only show if links exist */}
                {(member.linkedIn || member.reel) && (
                  <div className="flex items-center gap-3 pt-3">
                    {member.linkedIn && (
                      <a
                        href={member.linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F5F4F0] text-[#5C6B63] hover:bg-[#0077B5] hover:text-white hover:scale-110 transition-all duration-300"
                        aria-label={`${member.name} LinkedIn`}
                      >
                        <Linkedin className="w-4 h-4" />
                      </a>
                    )}
                    {member.reel && (
                      <a
                        href={member.reel}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F5F4F0] text-[#5C6B63] hover:bg-[#2E5C44] hover:text-white hover:scale-110 transition-all duration-300"
                        aria-label={`${member.name} Reel`}
                      >
                        <Play className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;